import {
  Database01Icon,
  SearchVisualIcon,
  Target03Icon,
} from '@terra/ui-icons';
import type { ToolConfig } from '../../tool-config-types';
import { renderExternalNetworkScannerCard } from '../external-network-scanner/external-network-scanner-card-renderer';
import { renderExternalNetworkMcpQueryCard } from '../external-network-mcp-query/external-network-mcp-query-card-renderer';
import { renderExternalNetworkScopeCard } from '../external-network-scope/external-network-scope-card-renderer';

const scannerToolBaseConfig = {
  icon: SearchVisualIcon,
  category: 'executable' as const,
  cardType: 'minimal' as const,
  hideInput: true,
  renderCard: renderExternalNetworkScannerCard,
};
const EXTERNAL_NETWORK_SCANNER_TOOL_ENTRIES = [
  [
    'mcp__terra__terra_scanner_passive_subdomain_enumeration',
    {
      name: 'Passive Subdomain Enumeration',
      runningText: 'Running passive subdomain enumeration...',
    },
  ],
  [
    'mcp__terra__terra_scanner_subdomain_permutation',
    {
      name: 'Subdomain Permutation',
      runningText: 'Running subdomain permutation...',
    },
  ],
  [
    'mcp__terra__terra_scanner_dns_resolution',
    { name: 'DNS Resolution', runningText: 'Running DNS resolution...' },
  ],
  [
    'mcp__terra__terra_scanner_reverse_dns',
    { name: 'Reverse DNS Lookup', runningText: 'Running reverse DNS...' },
  ],
  [
    'mcp__terra__terra_scanner_dns_bruteforce',
    { name: 'DNS Brute-force', runningText: 'Running DNS brute-force...' },
  ],
  [
    'mcp__terra__terra_scanner_host_discovery',
    { name: 'Host Discovery', runningText: 'Running host discovery...' },
  ],
  [
    'mcp__terra__terra_scanner_port_scan',
    { name: 'Port Scan', runningText: 'Running port scan...' },
  ],
  [
    'mcp__terra__terra_scanner_services_fingerprint',
    {
      name: 'Service Fingerprinting',
      runningText: 'Running service fingerprinting...',
    },
  ],
  [
    'mcp__terra__terra_scanner_exposed_asset_discovery',
    {
      name: 'Exposed Asset Discovery',
      runningText: 'Running exposed asset discovery...',
    },
  ],
  [
    'mcp__terra__terra_scanner_scan_get',
    { name: 'Scan Status', runningText: 'Fetching scan status...' },
  ],
  [
    'mcp__terra__terra_scanner_scan_activity_logs',
    {
      name: 'Scan Activity Logs',
      runningText: 'Fetching scan activity logs...',
    },
  ],
] as const;
const queryToolBaseConfig = {
  icon: Database01Icon,
  category: 'intelligence' as const,
  cardType: 'minimal' as const,
  hideInput: true,
  renderCard: renderExternalNetworkMcpQueryCard,
};
const EXTERNAL_NETWORK_QUERY_TOOL_ENTRIES = [
  [
    'mcp__terra__terra_external_networks_list',
    {
      name: 'List External Networks',
      runningText: 'Listing external networks...',
    },
  ],
  [
    'mcp__terra__terra_external_network_scans_list',
    { name: 'List Surface Scans', runningText: 'Listing surface scans...' },
  ],
  [
    'mcp__terra__terra_external_network_services_list',
    {
      name: 'List Network Services',
      runningText: 'Listing network services...',
    },
  ],
  [
    'mcp__terra__terra_external_network_softwares_list',
    { name: 'List Software', runningText: 'Listing software...' },
  ],
  [
    'mcp__terra__terra_external_network_signals_list',
    { name: 'List Surface Signals', runningText: 'Listing surface signals...' },
  ],
  [
    'mcp__terra__terra_external_network_signals_count',
    {
      name: 'Count Surface Signals',
      runningText: 'Counting surface signals...',
    },
  ],
  [
    'mcp__terra__terra_external_network_findings_list',
    {
      name: 'List Surface Findings',
      runningText: 'Listing surface findings...',
    },
  ],
  [
    'mcp__terra__terra_external_network_findings_count',
    {
      name: 'Count Surface Findings',
      runningText: 'Counting surface findings...',
    },
  ],
  [
    'mcp__terra__terra_external_network_domains_list',
    { name: 'List Domains', runningText: 'Listing domains...' },
  ],
  [
    'mcp__terra__terra_external_network_netblocks_list',
    { name: 'List Netblocks', runningText: 'Listing netblocks...' },
  ],
  [
    'mcp__terra__terra_findings_list',
    { name: 'List Findings', runningText: 'Listing findings...' },
  ],
  [
    'mcp__terra__terra_findings_get',
    { name: 'Get Finding', runningText: 'Fetching finding...' },
  ],
  [
    'mcp__terra__terra_findings_get_statistics',
    {
      name: 'Finding Statistics',
      runningText: 'Fetching finding statistics...',
    },
  ],
] as const;

const scopeToolBaseConfig = {
  icon: Target03Icon,
  category: 'executable' as const,
  cardType: 'minimal' as const,
  hideInput: true,
  renderCard: renderExternalNetworkScopeCard,
};
const EXTERNAL_NETWORK_SCOPE_TOOL_ENTRIES = [
  [
    'mcp__terra__terra_external_network_scope_domains_add',
    { name: 'Add Domains to Scope', runningText: 'Adding domains to scope...' },
  ],
  [
    'mcp__terra__terra_external_network_scope_netblocks_add',
    {
      name: 'Add Netblocks to Scope',
      runningText: 'Adding netblocks to scope...',
    },
  ],
  [
    'mcp__terra__terra_external_network_scope_domain_set',
    { name: 'Set Domain Scope', runningText: 'Updating domain scope...' },
  ],
  [
    'mcp__terra__terra_external_network_scope_netblock_set',
    { name: 'Set Netblock Scope', runningText: 'Updating netblock scope...' },
  ],
] as const;

function buildToolConfigMap(
  entries: ReadonlyArray<
    readonly [
      string,
      {
        name: string;
        runningText: string;
      },
    ]
  >,
  baseConfig: Pick<
    ToolConfig,
    'icon' | 'category' | 'cardType' | 'hideInput' | 'renderCard'
  >,
): Record<string, ToolConfig> {
  return Object.fromEntries(
    entries.map(([toolKey, toolMeta]) => [
      toolKey,
      {
        ...baseConfig,
        name: toolMeta.name,
        runningText: toolMeta.runningText,
      },
    ]),
  );
}

export const EXTERNAL_NETWORK_SCANNER_MCP_TOOL_CONFIGS = buildToolConfigMap(
  EXTERNAL_NETWORK_SCANNER_TOOL_ENTRIES,
  scannerToolBaseConfig,
);
export const EXTERNAL_NETWORK_QUERY_MCP_TOOL_CONFIGS = buildToolConfigMap(
  EXTERNAL_NETWORK_QUERY_TOOL_ENTRIES,
  queryToolBaseConfig,
);
export const EXTERNAL_NETWORK_SCOPE_MCP_TOOL_CONFIGS = buildToolConfigMap(
  EXTERNAL_NETWORK_SCOPE_TOOL_ENTRIES,
  scopeToolBaseConfig,
);
